import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { MODAL_FOOTER_PRIMARY_BUTTON_STYLE } from "@/components/base/modalFooterButtonStyles";

export function SourceFormField({
  label,
  required = false,
  children,
  hint,
}: {
  label: ReactNode;
  required?: boolean;
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="grid gap-1.5 lg:grid-cols-[168px_minmax(0,1fr)] lg:items-start lg:gap-4">
      <div className="flex min-h-9 items-center gap-1.5 text-sm font-semibold text-gray-800">
        {label}
        {required && <span className="text-red-400">*</span>}
      </div>
      <div className="min-w-0">
        {children}
        {hint && <div className="mt-1 text-[10px] leading-relaxed text-gray-400">{hint}</div>}
      </div>
    </div>
  );
}

export function SourceAuthorizationNotice() {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-amber-800">
      <div className="flex items-start gap-3">
        <i className="ri-information-line mt-0.5 shrink-0 text-base text-amber-500" />
        <div>
          <h3 className="text-xs font-semibold">AZ Authorization Required</h3>
          <p className="mt-1 text-xs leading-relaxed text-amber-700">
            The SharePoint site or Teams source you are adding must have its client_id authorized by
            AZ IT. Without this authorization, files cannot be indexed or accessed by the system.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SourceFormPageLayout({
  title,
  onBack,
  notice,
  children,
  primaryLabel,
  primaryDisabled,
  submitting = false,
  onPrimary,
}: {
  title: string;
  onBack: () => void;
  notice?: ReactNode;
  children: ReactNode;
  primaryLabel: string;
  primaryDisabled: boolean;
  submitting?: boolean;
  onPrimary: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[calc(100vh-7rem)] flex-col">
      <div className="mb-6">
        <button
          type="button"
          onClick={onBack}
          className="mb-3 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm text-gray-500 transition-colors hover:text-gray-800"
        >
          <i className="ri-arrow-left-line text-base" />
          {t("common.back")}
        </button>
        <h2 className="flex items-center gap-1.5 text-xl font-semibold text-gray-900">
          {title}
          <i className="ri-question-line text-sm text-gray-400" aria-hidden="true" />
        </h2>
      </div>

      <div className="grid w-full flex-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_302px]">
        <div className="flex min-w-0 flex-col gap-5">{children}</div>
        {notice && <aside className="xl:sticky xl:top-6">{notice}</aside>}
      </div>

      <div className="mt-8 grid w-full gap-6 border-t border-gray-100 pt-5 xl:grid-cols-[minmax(0,1fr)_302px]">
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-10 min-w-[96px] items-center justify-center rounded-lg border border-gray-200 bg-white px-5 text-sm font-normal text-gray-600 transition-colors hover:bg-gray-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={primaryDisabled}
            onClick={onPrimary}
            className="inline-flex h-10 min-w-[84px] items-center justify-center gap-1.5 rounded-lg px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            style={MODAL_FOOTER_PRIMARY_BUTTON_STYLE}
          >
            {submitting && <i className="ri-loader-4-line animate-spin" />}
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
